import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import HelpOutline from '@mui/icons-material/HelpOutline';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, SessionTimeout, type NavItem } from '@routes-plan/ui';
import { useMenu } from './MenuProvider';
import { useColorMode } from './context/ColorModeContext';
import { useScreenHelp } from './help/ScreenHelp';
import NotificationBell from './notifications/NotificationBell';
import { RuttiaIcon } from './icons/RuttiaIcon';
import { menuGlyph, moduleGlyph } from './icons/menuIcons';
import { menuTextKey, moduleTextKey } from '../utils/menuLabels';
import { useT } from '../i18n';
import { User } from '../models/user';
import { clearAuthToken } from '../utils/auth-storage';
import { buildAuthorizedModulesFromAuthUser } from '../utils/authorized-modules';
import { emitPermissionsRefreshed } from '../utils/permission-refresh';

interface WrapperProps {
  children: ReactNode;
}

const normalizeRoute = (route: string) => route.replace(/^\/+/, '').replace(/\/+$/, '');

/**
 * Estructura de la web de la empresa, la misma del panel y del portal (`AppLayout` de @routes-plan/ui):
 * menú lateral con los módulos y opciones que el rol de la persona tiene permitidos, barra superior con
 * ayuda, avisos, modo claro u oscuro y usuario, y cierre de sesión por inactividad.
 */
const Wrapper: React.FC<WrapperProps> = ({ children }) => {
  const t = useT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { modules, setModules } = useMenu();
  const { mode, setPreference } = useColorMode();
  const screenHelp = useScreenHelp();
  const [user, setUser] = useState(new User());
  const isReportsRoute = pathname.startsWith('/reports/');

  useEffect(() => {
    let alive = true;
    axios
      .get('user')
      .then(({ data }) => alive && data?.data && setUser(User.fromJSON(data.data)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // Una sesión vencida lleva al ingreso.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        if (error?.response?.status === 401) navigate('/login');
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      await axios.post('logout', {});
    } finally {
      clearAuthToken();
      sessionStorage.removeItem('isAuthenticated');
      sessionStorage.removeItem('modules');
      localStorage.removeItem('modules');
      navigate('/login');
    }
  }, [navigate]);

  const refreshPermissions = useCallback(async () => {
    try {
      const { data } = await axios.get('user');
      const authUser = data?.data ?? null;
      setUser(User.fromJSON(authUser));
      const refreshed = buildAuthorizedModulesFromAuthUser(authUser);
      setModules(refreshed);
      sessionStorage.setItem('modules', JSON.stringify(refreshed));
      localStorage.setItem('modules', JSON.stringify(refreshed));
      emitPermissionsRefreshed();
    } catch {
      /* se intenta de nuevo la próxima vez */
    }
  }, [setModules]);

  // Menú: inicio y, por cada módulo permitido, sus opciones (sin repetidas), en el orden configurado.
  const { navigation, routeOf } = useMemo(() => {
    const routes = new Map<string, string>([['home', '']]);
    const items: NavItem[] = [{ id: 'home', label: t('shell.home'), icon: <RuttiaIcon name="home" /> }];
    const sorted = [...(modules || [])].filter((m) => m && m.id && Array.isArray(m.menu) && m.menu.length > 0).sort((a, b) => a.order - b.order);
    const seenModules = new Set<string>();
    for (const m of sorted) {
      if (seenModules.has(String(m.id))) continue;
      seenModules.add(String(m.id));
      const seenRoutes = new Set<string>();
      const children: NavItem[] = [...m.menu]
        .filter((o) => o && o.route)
        .sort((a, b) => a.order - b.order)
        .filter((o) => {
          const r = normalizeRoute(o.route);
          if (seenRoutes.has(r)) return false;
          seenRoutes.add(r);
          return true;
        })
        .map((o) => {
          const route = normalizeRoute(o.route);
          const id = `r:${route}`;
          routes.set(id, route);
          const key = menuTextKey(o.route);
          const glyph = menuGlyph(o.route);
          return { id, label: key ? t(key) : o.name, icon: glyph ? <RuttiaIcon name={glyph} /> : undefined };
        });
      if (!children.length) continue;
      const key = moduleTextKey(m.name);
      // Cada módulo con su ícono propio (por el ícono guardado en la base, su nombre o sus opciones).
      const glyph = moduleGlyph(m.icon, m.name, m.menu.map((o) => o?.route));
      items.push({ id: `m:${m.id}`, label: key ? t(key) : m.name, icon: <RuttiaIcon name={glyph} />, children });
    }
    return { navigation: items, routeOf: routes };
  }, [modules, t]);

  // Opción activa: la ruta más larga que coincide con la dirección actual.
  const activeId = useMemo(() => {
    const path = normalizeRoute(pathname);
    if (!path) return 'home';
    let best: string | undefined;
    let bestLength = -1;
    routeOf.forEach((route, id) => {
      if (route && (path === route || path.startsWith(`${route}/`)) && route.length > bestLength) {
        best = id;
        bestLength = route.length;
      }
    });
    return best;
  }, [pathname, routeOf]);

  const topBar = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Tooltip title={screenHelp.guides.length ? t('screenHelp.title') : t('help.title')}>
        <IconButton
          aria-label={screenHelp.guides.length ? t('screenHelp.title') : t('help.title')}
          onClick={() => (screenHelp.guides.length ? screenHelp.openHelp() : navigate('/ayuda'))}
        >
          <HelpOutline />
        </IconButton>
      </Tooltip>
      <NotificationBell />
    </Box>
  );

  const fullName = (user.name || '').trim() || user.username || '';

  return (
    <>
      <AppLayout
        navigation={navigation}
        activeId={activeId}
        onNavigate={(item) => {
          const route = routeOf.get(item.id);
          if (route !== undefined) navigate(`/${route}`);
        }}
        search={topBar}
        colorMode={mode}
        onColorModeChange={(m) => setPreference(m)}
        user={{ name: fullName, subtitle: user.username }}
        userMenu={[
          {
            label: t('notifications.preferences'),
            icon: <NotificationsActiveOutlined fontSize="small" />,
            onClick: () => navigate('/notifications/preferences'),
          },
          { label: t('shell.refreshPermissions'), icon: <SyncOutlined fontSize="small" />, onClick: () => void refreshPermissions() },
        ]}
        onLogout={() => void logout()}
        storageKey="ruttia-web:sidebar"
      >
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            ...(isReportsRoute ? { height: { xs: 'calc(100dvh - 96px)', md: 'calc(100dvh - 112px)' }, overflow: 'hidden' } : {}),
          }}
        >
          {children}
        </Box>
      </AppLayout>
      <SessionTimeout idleMinutes={30} onTimeout={() => void logout()} />
    </>
  );
};

export default Wrapper;
